import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Row, Col, Card, Image, Button, Select, Descriptions, Tabs, 
  Tag, Rate, Breadcrumb, Spin, message, Space, Divider, Badge, Tooltip
} from 'antd';
import { 
  ShoppingCartOutlined, 
  HeartOutlined, 
  ShareAltOutlined,
  CheckCircleOutlined,
  TruckOutlined,
  SafetyOutlined,
  ReloadOutlined,
  StarOutlined,
  EyeOutlined,
  ZoomInOutlined,
  FireOutlined,
  ThunderboltOutlined,
  MinusOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { getProductBySlug } from '../services/productApi';
import { useNotificationContext } from '../components/NotificationContext';
import { BaseUrlAddress } from '../components/Initilization';
import "../assets/css/productdetail.css"

const { TabPane } = Tabs;
const { Option } = Select;

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const notif = useNotificationContext();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeTab, setActiveTab] = useState('description');

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await getProductBySlug(slug);
      if (response.data.ResultCode === 200 || response.data) {
        const productData = response.data.Obj || response.data;
        setProduct(productData);
        if (productData.variants && productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0]);
        }
        if (productData.images && productData.images.length > 0) {
          setSelectedImage(productData.images[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      message.error('مشکلی در دریافت اطلاعات محصول پیش آمد');
    } finally {
      setLoading(false);
    }
  };

  const handleVariantChange = (variantId) => {
    const variant = product.variants.find(v => v.id === variantId);
    setSelectedVariant(variant);
    setSelectedSize(null);
  };

  const handleSizeChange = (sizeId) => {
    const size = selectedVariant.sizes.find(s => s.size === sizeId);
    setSelectedSize(size);
  };

  const updateQuantity = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= (selectedVariant?.stock_quantity || product.numbers_in_stock || 10)) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    // For orderable products (is_orderable = true)
    if (product.is_orderable) {
      if (!selectedVariant && product.variants?.length > 0) {
        message.warning('لطفاً رنگ و کیفیت محصول را انتخاب کنید');
        return;
      }
      
      if (selectedVariant && selectedVariant.sizes && selectedVariant.sizes.length > 0 && !selectedSize) {
        message.warning('لطفاً سایز محصول را انتخاب کنید');
        return;
      }

      const cartItem = {
        id: Date.now(),
        productId: product.id,
        productSlug: product.slug,
        title: product.title,
        price: selectedSize ? selectedSize.total_price : (selectedVariant?.price || product.final_price),
        quantity: quantity,
        variant: selectedVariant ? {
          color: selectedVariant.color_name,
          quality: selectedVariant.quality_name,
          size: selectedSize?.size_name
        } : null,
        image: product.main_image_url
      };

      const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
      existingCart.push(cartItem);
      localStorage.setItem('cart', JSON.stringify(existingCart));
      window.dispatchEvent(new Event('cartUpdated'));

      notif.open({
        type: 'success',
        message: 'موفق',
        description: 'محصول به سبد خرید اضافه شد',
        placement: 'topRight'
      });
    } else {
      // For designed products - redirect to designer page
      message.info('این محصول نیاز به طراحی دارد، در حال انتقال به صفحه طراحی...');
      setTimeout(() => {
        navigate(`/designer/${product.slug}`);
      }, 1000);
    }
  };

  const getBadgeStyle = (type) => {
    switch(type) {
      case 'discount':
        return {
          background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(255,65,108,0.3)'
        };
      case 'new':
        return {
          background: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,180,219,0.3)'
        };
      case 'bestseller':
        return {
          background: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
          color: 'white',
          boxShadow: '0 4px 12px rgba(245,175,25,0.3)'
        };
      default:
        return {
          background: '#a23854',
          color: 'white'
        };
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>محصولی یافت نشد</h2>
        <Button onClick={() => navigate('/')}>بازگشت به صفحه اصلی</Button>
      </div>
    );
  }

  const mainImageUrl = selectedImage?.image || product.main_image_url;
  const discountPercent = product.discount_percent;

  return (
    <div style={{ direction: 'rtl', maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      
      {/* Breadcrumb */}
      <div style={{ marginBottom: 24, padding: '12px 0' }}>
        <Breadcrumb
          items={[
            { title: <a href="/" style={{ color: '#fafafa' }}>صفحه اصلی</a> },
            { title: <a href="/products" style={{ color: '#fafafa' }}>محصولات</a> },
            { title: <span style={{ color: '#a23854', fontWeight: 'bold' }}>{product.title}</span> }
          ]}
        />
      </div>

      {/* Equal height row using flex */}
      <Row gutter={[32, 32]} align="stretch">
        {/* Product Images Section */}
        <Col xs={24} md={10} style={{ display: 'flex' }}>
          <Card 
            className="light-glass-effect-card shaped-border"
            style={{ 
              width: '100%',
              borderRadius: 20,
              overflow: 'hidden'
            }}
            bodyStyle={{ padding: 24 }}
          >
            {/* Main Image */}
            <div style={{ 
              textAlign: 'center', 
              position: 'relative',
              borderRadius: 16,
              padding: 20,
              minHeight: 450,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.02)'
            }}>
              {mainImageUrl ? (
                <Image
                  src={(BaseUrlAddress + mainImageUrl).replace(/([^:]\/)\/+/g, '$1')}
                  alt={product.title}
                  style={{ 
                    maxHeight: 450, 
                    objectFit: 'contain',
                    transition: 'transform 0.3s ease'
                  }}
                  preview={{
                    mask: <div style={{ display: 'flex', gap: 8 }}>
                      <EyeOutlined /> <ZoomInOutlined />
                    </div>
                  }}
                />
              ) : (
                <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                  <ShoppingCartOutlined style={{ fontSize: 64 }} />
                </div>
              )}
            </div>
            
            {/* Thumbnails */}
            {product.images && product.images.length > 0 && (
              <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                {product.images.map((img, idx) => (
                  <div
                    key={img.id}
                    onClick={() => setSelectedImage(img)}
                    style={{
                      width: 70,
                      height: 70,
                      border: selectedImage?.id === img.id ? '2px solid #a23854' : '1px solid #ddd',
                      borderRadius: 10,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      background: '#fff'
                    }}
                  >
                    <img 
                  src={(BaseUrlAddress + img.image).replace(/([^:]\/)\/+/g, '$1')}
                      alt={img.alt_text} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Product Info Section */}
        <Col xs={24} md={14} style={{ display: 'flex' }}>
          <Card 
            className="light-glass-effect-card shaped-border"
            style={{ 
              width: '100%',
              borderRadius: 20
            }}
            bodyStyle={{ padding: 28 }}
          >
            {/* Modern Badges */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              {product.is_new && (
                <div style={getBadgeStyle('new')}>
                  <span style={{ padding: '4px 14px', display: 'inline-block', fontSize: 12, fontWeight: 'bold', borderRadius: 20 }}>
                    جدید
                  </span>
                </div>
              )}
              {product.is_best_seller && (
                <div style={getBadgeStyle('bestseller')}>
                  <span style={{ padding: '4px 14px', display: 'inline-block', fontSize: 12, fontWeight: 'bold', borderRadius: 20 }}>
                    <FireOutlined style={{ marginLeft: 4 }} /> پرفروش‌ترین
                  </span>
                </div>
              )}
              {discountPercent > 0 && (
                <div style={getBadgeStyle('discount')}>
                  <span style={{ padding: '4px 14px', display: 'inline-block', fontSize: 12, fontWeight: 'bold', borderRadius: 20 }}>
                    <ThunderboltOutlined style={{ marginLeft: 4 }} /> تخفیف {discountPercent}%
                  </span>
                </div>
              )}
            </div>

            {/* Title - Redish color */}
            <h1 style={{ 
              fontFamily: 'Yekan', 
              fontSize: '1.8rem', 
              marginBottom: 16, 
              color: '#a23854',
              fontWeight: 'bold'
            }}>
              {product.title}
            </h1>

            {/* Short Description - Dark text */}
            {product.short_description && (
              <div style={{ 
                marginBottom: 24, 
                padding: '14px 18px', 
                background: 'rgba(162,56,84,0.06)',
                borderRadius: 12,
                borderRight: '3px solid #a23854'
              }}>
                <p style={{ margin: 0, lineHeight: 1.8, color: '#555', fontSize: 13 }}>
                  {product.short_description}
                </p>
              </div>
            )}

            {/* Price Section */}
            <div style={{ marginBottom: 24, padding: '12px 0', borderTop: '1px solid #a23854', borderBottom: '1px solid #a23854' }}>
              {discountPercent > 0 ? (
                <>
                  <span style={{ fontSize: 28, color: '#a23854', fontWeight: 'bold', marginLeft: 12 }}>
                    {product.final_price.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 16, color: '#999', textDecoration: 'line-through', marginLeft: 8 }}>
                    {product.base_price.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 14, color: '#666' }}>تومان</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: 28, color: '#a23854', fontWeight: 'bold' }}>
                    {product.base_price.toLocaleString()}
                  </span>
                  <span style={{ fontSize: 14, color: '#666', marginRight: 4 }}>تومان</span>
                </>
              )}
            </div>

            {/* Variant Selection - Only show for orderable products */}
            {product.is_orderable && product.variants && product.variants.length > 0 && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 10, color: '#333', fontWeight: 'bold', fontSize: 14 }}>انتخاب رنگ و کیفیت:</div>
                  <Select
                    value={selectedVariant?.id}
                    onChange={handleVariantChange}
                    style={{ width: '100%' }}
                    size="large"
                  >
                    {product.variants.map(variant => (
                      <Option key={variant.id} value={variant.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ 
                            width: 18, 
                            height: 18, 
                            borderRadius: '50%', 
                            background: variant.color_hex || '#ccc',
                            border: '1px solid #ddd'
                          }} />
                          <span>{variant.color_name} - {variant.quality_name}</span>
                          <span style={{ color: '#a23854' }}>{variant.price.toLocaleString()} تومان</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Size Selection */}
                {selectedVariant && selectedVariant.sizes && selectedVariant.sizes.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 10, color: '#333', fontWeight: 'bold', fontSize: 14 }}>انتخاب سایز:</div>
                    <Space wrap size={10}>
                      {selectedVariant.sizes.map(size => (
                        <Button
                          key={size.size}
                          onClick={() => handleSizeChange(size.size)}
                          type={selectedSize?.size === size.size ? 'primary' : 'default'}
                          style={{
                            minWidth: 65,
                            height: 42,
                            borderRadius: 10,
                            fontWeight: 'bold',
                            background: selectedSize?.size === size.size ? '#a23854' : 'white',
                            borderColor: '#a23854',
                            color: selectedSize?.size === size.size ? 'white' : '#a23854'
                          }}
                        >
                          {size.size_name}
                        </Button>
                      ))}
                    </Space>
                  </div>
                )}

                {/* Quantity Selector */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 10, color: '#333', fontWeight: 'bold', fontSize: 14 }}>تعداد:</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Button 
                      icon={<MinusOutlined />}
                      onClick={() => updateQuantity(-1)}
                      disabled={quantity <= 1}
                    />
                    <span style={{ 
                      width: 50, 
                      textAlign: 'center', 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: '#333'
                    }}>
                      {quantity}
                    </span>
                    <Button 
                      icon={<PlusOutlined />}
                      onClick={() => updateQuantity(1)}
                      disabled={quantity >= (selectedVariant?.stock_quantity || product.numbers_in_stock || 10)}
                    />
                  </div>
                </div>

                {/* Stock Status */}
                <div style={{ marginBottom: 24 }}>
                  {(selectedVariant?.stock_quantity > 0 || product.numbers_in_stock > 0) ? (
                    <span style={{ color: '#1d552a', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CheckCircleOutlined />
                      <span>موجود در انبار</span>
                    </span>
                  ) : (
                    <span style={{ color: '#ff4d4f' }}>ناموجود</span>
                  )}
                </div>
              </>
            )}

            {/* Message for designed products */}
            {!product.is_orderable && (
              <div style={{ 
                marginBottom: 24, 
                padding: 16, 
                background: 'rgba(162,56,84,0.1)', 
                borderRadius: 12,
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, color: '#a23854' }}>
                  این محصول قابل طراحی است. پس از ثبت سفارش، می‌توانید طرح دلخواه خود را روی آن اعمال کنید.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Button 
                type="primary" 
                size="large" 
                icon={<ShoppingCartOutlined />}
                onClick={handleAddToCart}
                disabled={product.is_orderable && ((selectedVariant?.stock_quantity || product.numbers_in_stock) === 0)}
                style={{ 
                  flex: 2,
                  height: 48,
                  background: '#a23854',
                  borderColor: '#a23854',
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 'bold'
                }}
              >
                {product.is_orderable ? 'افزودن به سبد خرید' : 'شروع طراحی'}
              </Button>
              
              <Tooltip title="علاقه‌مندی">
                <Button size="large" icon={<HeartOutlined />} style={{ height: 48, width: 48, borderRadius: 12 }} />
              </Tooltip>
              <Tooltip title="اشتراک گذاری">
                <Button size="large" icon={<ShareAltOutlined />} style={{ height: 48, width: 48, borderRadius: 12 }} />
              </Tooltip>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Features and Benefits - Equal height cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 40 }}>
        {[
          { icon: <TruckOutlined />, title: "ارسال سریع", desc: "۲۴ تا ۴۸ ساعت کاری" },
          { icon: <SafetyOutlined />, title: "ضمانت کیفیت", desc: "بازگشت وجه در صورت عدم رضایت" },
          { icon: <ReloadOutlined />, title: "گارانتی بازگشت", desc: "۷ روز ضمانت بازگشت کالا" },
          { icon: <StarOutlined />, title: "پشتیبانی ۲۴/۷", desc: "همیشه در کنار شما هستیم" }
        ].map((item, idx) => (
          <Col xs={12} sm={6} key={idx} style={{ display: 'flex' }}>
            <Card 
              className="light-glass-effect-card shaped-border"
              style={{ 
                width: '100%',
                textAlign: 'center',
                borderRadius: 16,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '20px 16px' }}
              hoverable
            >
              <div style={{ fontSize: 32, color: '#a23854', marginBottom: 12 }}>
                {item.icon}
              </div>
              <div style={{ color: '#333', fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>
                {item.title}
              </div>
              <div style={{ color: '#666', fontSize: 11 }}>
                {item.desc}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Tabs for Details */}
      <Card 
        className="light-glass-effect-card shaped-border"
        style={{ 
          marginTop: 40,
          borderRadius: 20
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs 
          defaultActiveKey="features"
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ padding: '0 24px' }}
          items={[
            {
              key: 'features',
              label: <span style={{ fontFamily: 'Yekan', fontSize: 15 }}>مشخصات فنی</span>,
              children: (
                <div style={{ padding: '4px' }}>
                  {product.features && product.features.length > 0 ? (
                    <Descriptions 
                      bordered 
                      column={{ xs: 1, sm: 2 }}
                      labelStyle={{ 
                        background: 'rgba(162,56,84,0.1)', 
                        color: '#a23854',
                        fontWeight: 'bold'
                      }}
                      contentStyle={{ color: '#444' }}
                    >
                      {product.features.map(feature => (
                        <Descriptions.Item key={feature.id} label={feature.feature_name}>
                          {feature.feature_value}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                  ) : (
                    <p style={{ color: '#666', textAlign: 'center', padding: 40 }}>
                      مشخصات فنی برای این محصول ثبت نشده است.
                    </p>
                  )}
                </div>
              )
            },
            {
              key: 'description',
              label: <span style={{ fontFamily: 'Yekan', fontSize: 15 }}>توضیحات محصول</span>,
              children: (
                <div style={{ padding: '4px', color: '#444', lineHeight: 2 }}>
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: product.description?.replace(/\n/g, '<br/>') || 'توضیحاتی برای این محصول ثبت نشده است.' 
                    }}
                    style={{ textAlign: 'justify' }}
                  />
                </div>
              )
            },
            {
              key: 'videos',
              label: <span style={{ fontFamily: 'Yekan', fontSize: 15 }}>ویدیوها</span>,
              children: (
                <div style={{ padding: '24px' }}>
                  {product.videos && product.videos.length > 0 ? (
                    <Row gutter={[16, 16]}>
                      {product.videos.map(video => (
                        <Col xs={24} md={12} key={video.id}>
                          <video 
                            src={video.video} 
                            controls 
                            style={{ width: '100%', borderRadius: 12 }}
                            poster={video.cover_image}
                          />
                          <p style={{ marginTop: 8, textAlign: 'center', color: '#666' }}>{video.title}</p>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <p style={{ color: '#666', textAlign: 'center', padding: 40 }}>
                      ویدیویی برای این محصول ثبت نشده است.
                    </p>
                  )}
                </div>
              )
            }
          ]}
        />
      </Card>

      <style dangerouslySetInnerHTML={{
        __html: `
          .ant-tabs-tab {
            color: #666 !important;
          }
          
          .ant-tabs-tab-active {
            color: #a23854 !important;
          }
          
          .ant-tabs-ink-bar {
            background: #a23854 !important;
          }
          
          .ant-select-selector {
            background: white !important;
            border-color: #ddd !important;
          }
          
          .ant-select-selection-item {
            color: #333 !important;
          }
          
          .ant-select-arrow {
            color: #a23854 !important;
          }
          
          .ant-select-selector:hover {
            border-color: #a23854 !important;
          }
          
          .ant-input-number {
            background: white !important;
          }
          
          .ant-card-body {
            color: #444;
          }
        `
      }} />
    </div>
  );
};

export default ProductDetail;